
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { parseSupabaseError } from '@/utils/errorHandling';
import { secureLogout } from '@/utils/auth-logout';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string, phone?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
  checkEmailExists: (email: string) => Promise<boolean>;
  checkPhoneExists: (phone: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let initialSessionChecked = false;

    // Set up auth state listener FIRST (Supabase recommended pattern)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          // Only set loading to false if we've already checked the initial session
          // This prevents race conditions on external navigations (like Stripe return)
          if (initialSessionChecked) {
            setLoading(false);
          }
        }
      }
    );

    const initializeAuth = async () => {
      try {
        // Check if session should be cleared (rememberMe was false on last login)
        const shouldClearSession = sessionStorage.getItem('clear_session_on_close');
        if (shouldClearSession === 'true') {
          // Clear session and sign out
          await supabase.auth.signOut();
          sessionStorage.removeItem('clear_session_on_close');
          if (mounted) {
            setSession(null);
            setUser(null);
          }
          initialSessionChecked = true;
          if (mounted) {
            setLoading(false);
          }
          return;
        }

        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          
          // BACKUP: Profile será criado automaticamente pelo trigger
          // Se falhar, tentamos criar no próximo login
          if (currentSession?.user) {
            setTimeout(async () => {
              try {
                const { data: profileCheck } = await supabase
                  .from('profiles')
                  .select('id')
                  .eq('id', currentSession.user.id)
                  .maybeSingle();

                if (!profileCheck) {
                  // Criar profile manualmente se trigger falhou
                  await supabase.from('profiles').insert({
                    id: currentSession.user.id,
                    display_name: currentSession.user.user_metadata?.display_name || currentSession.user.email?.split('@')[0],
                    email: currentSession.user.email,
                    phone: currentSession.user.user_metadata?.phone || null
                  });
                }
              } catch (err) {
                console.warn('Profile será criado automaticamente:', err);
              }
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        initialSessionChecked = true;
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_email_exists', { 
        email_to_check: email 
      });
      
      if (error) {
        console.error('[Validation] Erro ao verificar email:', error);
        return false;
      }
      
      return data || false;
    } catch (error) {
      console.error('[Validation] Erro ao verificar email:', error);
      return false;
    }
  };

  const checkPhoneExists = async (phone: string): Promise<boolean> => {
    try {
      // Normalizar telefone (apenas dígitos)
      const normalizedPhone = phone.replace(/\D/g, '');
      
      const { data, error } = await supabase.rpc('check_phone_exists', { 
        phone_to_check: normalizedPhone 
      });
      
      if (error) {
        console.error('[Validation] Erro ao verificar telefone:', error);
        return false;
      }
      
      return data || false;
    } catch (error) {
      console.error('[Validation] Erro ao verificar telefone:', error);
      return false;
    }
  };

  const signUp = async (email: string, password: string, displayName?: string, phone?: string) => {
    try {
      console.log('[AuthContext] Starting signup:', { email, displayName, hasPhone: !!phone });

      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            display_name: displayName || email.split('@')[0],
            phone: phone || null,
          }
        }
      });

      if (error) {
        const isEmptyError = !error.message && Object.keys(error).length === 0;
        
        console.error('[SignUp] Erro ao criar conta:', {
          isEmpty: isEmptyError,
          error: JSON.stringify(error),
          errorKeys: Object.keys(error),
          message: error.message,
          status: error.status
        });
        
        if (isEmptyError) {
          toast.error('Servidor sobrecarregado. Tentando novamente...');
          return { error: { message: '504', status: 504 } };
        }
        
        const parsedError = parseSupabaseError(error);
        
        // Detectar timeout/504 para permitir retry
        const isTimeout = error.message?.includes('504') || 
                         error.message?.includes('timeout') ||
                         error.message?.includes('deadline exceeded') ||
                         error.status === 504;
        
        if (isTimeout) {
          // Retornar erro identificável para retry
          return { error: { ...error, status: 504, message: error.message || 'Timeout ao criar conta' } };
        }
        
        // Mensagens específicas para erros comuns (não timeout)
        if (error.message?.includes('User already registered')) {
          toast.error('Este email já está cadastrado. Faça login.');
        } else if (error.message?.includes('rate limit') || parsedError.isRateLimit) {
          toast.error(parsedError.message || 'Muitas tentativas. Aguarde alguns minutos.');
        } else {
          const errorMessage = error.message || parsedError.message || 'Erro ao criar conta. Tente novamente.';
          toast.error(errorMessage);
        }
        
        return { error };
      }

      if (!data.user) {
        console.error('[SignUp] Nenhum usuário criado');
        toast.error('Falha ao criar usuário. Tente novamente.');
        return { error: new Error('Falha ao criar usuário') };
      }

      console.log('[SignUp] Conta criada com sucesso:', {
        userId: data.user.id,
        hasSession: !!data.session
      });

      // Feedback de sucesso
      toast.success('Conta criada com sucesso!');
      
      return { error: null };
      
    } catch (error: any) {
      console.error('[SignUp] Exceção não tratada:', error);
      
      // Tratar exceções de forma robusta
      const errorMessage = error?.message || 'Erro ao criar conta. Tente novamente.';
      toast.error(errorMessage);
      
      return { error };
    }
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      const parsedError = parseSupabaseError(error);
      toast.error(parsedError.message);
    } else {
      // Supabase uses localStorage by default for session persistence
      // If rememberMe is false, we set a flag to clear session on browser close
      if (!rememberMe) {
        // Store flag in sessionStorage (cleared when browser closes)
        sessionStorage.setItem('clear_session_on_close', 'true');
        console.log('[Auth] Session will be cleared when browser closes');
      } else {
        // Remove flag if present
        sessionStorage.removeItem('clear_session_on_close');
      }
    }

    return { error };
  };

  const signOut = async () => {
    try {
      await secureLogout();
      toast.success('Logout realizado com sucesso');
    } catch (error) {
      console.error('[Auth] Logout error');
      toast.error('Erro ao fazer logout. Tente novamente.');
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) {
      const parsedError = parseSupabaseError(error);
      toast.error(parsedError.message);
    } else {
      toast.success('Verifique seu email para redefinir a senha!');
    }

    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      return { error };
    } catch (error) {
      console.error('Error in updatePassword:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    checkEmailExists,
    checkPhoneExists,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
